import React, {useMemo} from 'react'
import styles from './Overview.module.scss'
import {useUIGameEndContext} from '../../../../../contexts/UIGameEndContext.tsx'
import {StatsTable} from '../../StatsTable/index.ts'
import {VictoryPointDataController, VictoryPointStateReader} from '@colonist/game-base-types'
import {type TranslationObject} from '@colonist/i18n'
// eslint-disable-next-line no-restricted-imports
import {navigateToPage} from '../../../../../../../../code/client/shared/scripts/ui_utils/ClientUtils.ts'
import {type ImageURL} from '@colonist/types'
import {VICTORY_POINT_END_GAME_TIP, VICTORY_POINT_IMAGES} from '@colonist/ui-game'
import StatMMRChange from '@static/images/stat_mmr_change.svg'
import {useGameStore} from '../../../../../store/GameStoreContext.ts'

export const Overview = () => {
    const {playersByRank, userRankedStatesByColor} = useUIGameEndContext()
    const isRanked = useGameStore((state) => state.gameDetails.isRanked)
    const allKeys = useMemo(() => Array.from(new Set(playersByRank.flatMap((player) =>
        Object.keys(player.victoryPoints).map((key) => Number(key)),),),).sort(), [playersByRank])

    const headers = useMemo(() => {
        const items: {imagePath: ImageURL, tooltip: TranslationObject, onClick?: VoidFunction}[] = []
        allKeys.forEach((key) => {
            const victoryPointData = VictoryPointDataController.getVictoryPointByType(Number(key))
            if(victoryPointData) {
                items.push({
                    imagePath: VICTORY_POINT_IMAGES[victoryPointData.victoryPointType],
                    tooltip: {key: VICTORY_POINT_END_GAME_TIP[victoryPointData.victoryPointType]},
                })
            }
        })
        if(isRanked) {
            items.push({
                imagePath: StatMMRChange,
                tooltip: {key: 'strings:game.endGame.overview.mmrChange'},
                onClick: () => {
                    navigateToPage('/leaderboards', true)
                },
            })
        }
        return items
    }, [allKeys])

    const rows = useMemo(() => playersByRank.map((player) => {
        const row: (number)[] = []
        allKeys.forEach((key) => {
            const calculatedPoint = VictoryPointStateReader.totalVictoryPointsOfType(player.victoryPoints, [Number(key)])
            row.push(calculatedPoint || 0)
        })
        if(isRanked) {
            const userRankedState = userRankedStatesByColor[player.color]
            const mmrChange = userRankedState?.getEndGameData().amount
            const numericalMMRChange = parseFloat(`${mmrChange}`)
            if(numericalMMRChange) {
                row.push(numericalMMRChange)
            } else {
                row.push(0)
            }
        }
        return row
    }), [playersByRank, allKeys])

    const rowProps = isRanked ? {
        [rows[0].length - 1]: {
            rowRenderer: (row) => {
                const rowValue = row > 0 ? `+${row}` : row < 0 ? row : '-'
                const rowClassName = row > 0 ? styles.textGreen : row < 0 ? styles.textRed : ''
                return <span className={rowClassName}>
                    {rowValue}
                </span>
            },
            disableRowHighlight: true,
        },
    } : {}

    return <>
        <StatsTable enableHighlight headers={headers} rowProps={rowProps} rows={rows}/>
    </>
}
